import { useRef, useState, useMemo, useCallback } from 'react';
import { useReducedMotion, type HTMLMotionProps, type Transition } from 'framer-motion';
import { CloudinaryImageFields } from '../CloudinaryImageWrapper';
type MotionAnimationProps = Partial<Pick<HTMLMotionProps<'div'>, 'animate' | 'transition'>>;
type UseInfiniteScrollAnimationProps = {
  images: CloudinaryImageFields[];
  imageWidth: number;
  gap: number;
  speed: number;
  animatedDivRef: React.RefObject<HTMLDivElement | null>;
  containerWidth: number;
};

/**
 * gets the current X translation value from an element's computed transform
 * returns 0 if no transform is applied
 */
const getTransformX = (element: HTMLElement | null): number => {
  if (!element) return 0;

  // get the computed transform matrix from CSS
  const transform = window.getComputedStyle(element).transform;

  // transform value is a string like 'matrix(1, 0, 0, 1, -370.45, 0)'
  // we need to parse the string to get the X translation value
  if (transform && transform !== 'none') {
    const matrix = new DOMMatrix(transform);

    // 'e' property contains the X translation value
    return matrix.e;
  }
  return 0;
};
export const useInfiniteScrollAnimation = ({
  images,
  imageWidth,
  gap,
  speed,
  animatedDivRef,
  containerWidth
}: UseInfiniteScrollAnimationProps) => {
  const shouldReduceMotion = useReducedMotion();
  // stores the X position when auto-scroll is paused
  const pausedXRef = useRef<number | null>(null);
  const [isPaused, setIsPaused] = useState(false);

  // total width of one complete set of images (used for infinite loop calculations)
  const imageSetWidth = useMemo(() => {
    return imageWidth * images.length + gap * images.length;
  }, [images.length, imageWidth, gap]);

  // calculate how many repeated sets we need (minimum 3 for smooth wraparound scrolling)
  const totalImageSets = useMemo(() => {
    // if we don’t yet know the container or content width, fall back to minimum sets
    if (containerWidth <= 0 || imageSetWidth <= 0) {
      return 3;
    }

    // ensure at least 3 sets, but add enough sets to fully fill the container width
    // Math.ceil(containerWidth / imageSetWidth) determines how many sets are needed to fill the container
    // +2 adds extra buffer sets to allow smooth wrapping without visible gaps
    return Math.max(3, Math.ceil(containerWidth / imageSetWidth) + 2);
  }, [containerWidth, imageSetWidth]);
  const scrollableImages = useMemo(() => Array.from({
    length: totalImageSets
  }, () => [...images]).flat(), [images, totalImageSets]);

  // time in seconds for one complete scroll cycle (one image set width)
  const animationDuration = useMemo(() => {
    // distance / speed = time
    return imageSetWidth / speed;
  }, [imageSetWidth, speed]);
  const animationProps = useMemo(() => {
    if (shouldReduceMotion) {
      return {};
    }

    // when paused, freeze at the captured position to allow manual scrolling
    if (isPaused && pausedXRef.current !== null) {
      return {
        animate: {
          x: pausedXRef.current
        },
        transition: {
          duration: 0
        }
      } satisfies MotionAnimationProps;
    }
    return {
      animate: {
        x: -imageSetWidth
      },
      transition: {
        duration: animationDuration,
        repeat: Infinity,
        repeatType: 'loop',
        ease: 'linear'
      } satisfies Transition
    } satisfies MotionAnimationProps;
  }, [imageSetWidth, animationDuration, shouldReduceMotion, isPaused]);
  const capturePositionAndPause = useCallback(() => {
    // get the current animated position before pausing so we can freeze there
    pausedXRef.current = getTransformX(animatedDivRef.current);
    setIsPaused(true);
  }, [animatedDivRef]);
  return {
    animationProps,
    scrollableImages,
    imageSetWidth,
    animationDuration,
    isPaused,
    capturePositionAndPause
  };
};